import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import { SideNav } from "./components/SideNav";
import {
  APPS,
  AppId,
  ScreenId,
  appUrl,
  canAccessApp,
  canAccessScreen,
  getApp,
  logoUrl,
  screensOf,
} from "./config/apps";
import { UserTeam, teamLabel } from "./api/meApi";
import { WikiSummary, downloadWiki, fetchActiveWikis } from "./api/wikiApi";
import { Register } from "./pages/Register";
import { AuthProvider, useAuth } from "./providers/AuthProvider";
import "./styles/index.css";

type ScreenComponents = Partial<Record<ScreenId, React.ComponentType>>;

const AccessDenied: React.FC<{ what: string; role?: string }> = ({ what, role }) => {
  const { roles, username } = useAuth();
  const elsewhere = APPS.find((app) => canAccessApp(roles, app));
  return (
    <main className="home-hero">
      <h2>Access denied</h2>
      <p>
        {username ? `${username} doesn't` : "You don't"} have access to {what}. Ask an
        administrator for {role ? <>the <code>{role}</code> role</> : "access"}.
      </p>
      {elsewhere && (
        <p>
          <a href={appUrl(elsewhere.id, "/")}>Go to {elsewhere.label}</a>
        </p>
      )}
    </main>
  );
};

const WikiMenu: React.FC<{ wikis: WikiSummary[]; token?: string }> = ({ wikis, token }) => {
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState<string>();
  const ref = useRef<HTMLDivElement>(null);

  // The API already orders by category, so a category's wikis are adjacent.
  const groups = wikis.reduce<{ key: string; title: string | null; wikis: WikiSummary[] }[]>((acc, wiki) => {
    const key = wiki.categoryId ?? "";
    const last = acc[acc.length - 1];
    if (last?.key === key) last.wikis.push(wiki);
    else acc.push({ key, title: wiki.categoryText, wikis: [wiki] });
    return acc;
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div className="profile-wrap wiki-wrap" ref={ref}>
      <button
        type="button"
        className="wiki-button"
        aria-label={`Wikis (${wikis.length})`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5v-15ZM4 20.5A2.5 2.5 0 0 0 6.5 23H20v-2"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className="profile-dropdown wiki-dropdown" role="menu">
          <div className="profile-team">
            <span className="profile-team-label">Wikis</span>
          </div>
          <div className="wiki-columns">
            {groups.map((group) => (
              <div className="wiki-group" key={group.key}>
                {group.title && <span className="wiki-group-heading">{group.title}</span>}
                {group.wikis.map((wiki) => (
                  <button
                    type="button"
                    className={`wiki-item${group.title ? " wiki-subitem" : ""}`}
                    role="menuitem"
                    key={wiki.id}
                    title={`Download ${wiki.fileName}`}
                    onClick={() => {
                      if (!token) return;
                      setFailed(undefined);
                      downloadWiki(token, wiki).catch(() => setFailed(wiki.displayName));
                    }}
                  >
                    {wiki.displayName}
                  </button>
                ))}
              </div>
            ))}
          </div>
          {failed && <div className="profile-user">Could not download {failed}.</div>}
        </div>
      )}
    </div>
  );
};

const ProfileMenu: React.FC<{
  username?: string;
  team?: UserTeam | null;
  onLogout: () => void;
}> = ({ username, team, onLogout }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div className="profile-wrap" ref={ref}>
      <button
        type="button"
        className="profile-avatar"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {(username || "?").charAt(0).toUpperCase()}
      </button>
      {open && (
        <div className="profile-dropdown" role="menu">
          {username && <div className="profile-user">{username}</div>}
          <div className="profile-team">
            <span className="profile-team-label">Team</span>
            {/* undefined is still loading; null means the lookup answered "none". */}
            <span
              className={`profile-team-value${team === null ? " profile-team-empty" : ""}`}
              title={team?.id ?? undefined}
            >
              {team === undefined ? "\u2026" : teamLabel(team) ?? "No team assigned"}
            </span>
          </div>
          <button
            type="button"
            role="menuitem"
            className="profile-tab"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path
                d="M3 9C3 9.19891 3.07902 9.38968 3.21967 9.53033C3.36032 9.67098 3.55109 9.75 3.75 9.75H9.4425L7.7175 11.4675C7.6472 11.5372 7.59141 11.6202 7.55333 11.7116C7.51525 11.803 7.49565 11.901 7.49565 12C7.49565 12.099 7.51525 12.197 7.55333 12.2884C7.59141 12.3798 7.6472 12.4628 7.7175 12.5325C7.78722 12.6028 7.87017 12.6586 7.96157 12.6967C8.05296 12.7347 8.15099 12.7543 8.25 12.7543C8.34901 12.7543 8.44704 12.7347 8.53843 12.6967C8.62983 12.6586 8.71278 12.6028 8.7825 12.5325L11.7825 9.5325C11.8508 9.46117 11.9043 9.37706 11.94 9.285C12.015 9.1024 12.015 8.8976 11.94 8.715C11.9043 8.62294 11.8508 8.53883 11.7825 8.4675L8.7825 5.4675C8.71257 5.39757 8.62955 5.3421 8.53819 5.30426C8.44682 5.26641 8.34889 5.24693 8.25 5.24693C8.15111 5.24693 8.05318 5.26641 7.96181 5.30426C7.87045 5.3421 7.78743 5.39757 7.7175 5.4675C7.64757 5.53743 7.5921 5.62045 7.55426 5.71181C7.51641 5.80318 7.49693 5.90111 7.49693 6C7.49693 6.09889 7.51641 6.19682 7.55426 6.28819C7.5921 6.37955 7.64757 6.46257 7.7175 6.5325L9.4425 8.25H3.75C3.55109 8.25 3.36032 8.32902 3.21967 8.46967C3.07902 8.61032 3 8.80109 3 9ZM12.75 1.5H5.25C4.65326 1.5 4.08097 1.73705 3.65901 2.15901C3.23705 2.58097 3 3.15326 3 3.75V6C3 6.19891 3.07902 6.38968 3.21967 6.53033C3.36032 6.67098 3.55109 6.75 3.75 6.75C3.94891 6.75 4.13968 6.67098 4.28033 6.53033C4.42098 6.38968 4.5 6.19891 4.5 6V3.75C4.5 3.55109 4.57902 3.36032 4.71967 3.21967C4.86032 3.07902 5.05109 3 5.25 3H12.75C12.9489 3 13.1397 3.07902 13.2803 3.21967C13.421 3.36032 13.5 3.55109 13.5 3.75V14.25C13.5 14.4489 13.421 14.6397 13.2803 14.7803C13.1397 14.921 12.9489 15 12.75 15H5.25C5.05109 15 4.86032 14.921 4.71967 14.7803C4.57902 14.6397 4.5 14.4489 4.5 14.25V12C4.5 11.8011 4.42098 11.6103 4.28033 11.4697C4.13968 11.329 3.94891 11.25 3.75 11.25C3.55109 11.25 3.36032 11.329 3.21967 11.4697C3.07902 11.6103 3 11.8011 3 12V14.25C3 14.8467 3.23705 15.419 3.65901 15.841C4.08097 16.2629 4.65326 16.5 5.25 16.5H12.75C13.3467 16.5 13.919 16.2629 14.341 15.841C14.7629 15.419 15 14.8467 15 14.25V3.75C15 3.15326 14.7629 2.58097 14.341 2.15901C13.919 1.73705 13.3467 1.5 12.75 1.5Z"
                fill="currentColor"
              />
            </svg>
            <span className="profile-tab-text">Logout</span>
          </button>
        </div>
      )}
    </div>
  );
};

const Shell: React.FC<{ id: AppId; components: ScreenComponents }> = ({
  id,
  components,
}) => {
  const { authenticated, logout, roles, team, token, username } = useAuth();
  const [wikis, setWikis] = useState<WikiSummary[]>([]);
  const app = getApp(id);
  const screens = screensOf(id);
  const visible = screens.filter((screen) => canAccessScreen(roles, screen));

  useEffect(() => {
    document.title = `${app.label} | Bite Test`;
  }, [app.label]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetchActiveWikis(token)
      .then((active) => {
        if (!cancelled) setWikis(active);
      })
      // The icon is a convenience; with no answer it stays hidden.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // Only the first token matters; refreshes carry the same claims.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(token)]);

  if (!authenticated) {
    return <p style={{ padding: 32 }}>Redirecting to sign in&hellip;</p>;
  }

  return (
    <BrowserRouter>
      <header className="app-header">
        <img src={logoUrl()} alt="BITE Test" />
        <h1>BITE Test</h1>
        {wikis.length > 0 && <WikiMenu wikis={wikis} token={token} />}
        <ProfileMenu username={username} team={team} onLogout={logout} />
      </header>
      <div className="app-body">
        <SideNav current={id} />
        <div className="app-content">
          {visible.length === 0 ? (
            <AccessDenied what={app.label} />
          ) : (
            <Switch>
              {screens.map((screen) => {
                const Screen = components[screen.id];
                if (!Screen) return null;
                return (
                  <Route key={screen.id} exact path={screen.path}>
                    {canAccessScreen(roles, screen) ? (
                      <Screen />
                    ) : (
                      <AccessDenied what={screen.label} role={screen.role} />
                    )}
                  </Route>
                );
              })}
              {/* Unknown paths, and the bare root of a multi-screen app, land on the first screen the user may open. */}
              <Redirect to={visible[0].path} />
            </Switch>
          )}
        </div>
      </div>
    </BrowserRouter>
  );
};

/** Mounts one bite-test app: shared shell + navigation around that app's screens. */
export function renderApp(id: AppId, components: ScreenComponents): void {
  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement,
  );
  // The one screen reachable without signing in; Keycloak's Register link lands here.
  if (id === "api" && window.location.pathname.replace(/\/+$/, "") === "/register") {
    root.render(
      <React.StrictMode>
        <Register />
      </React.StrictMode>,
    );
    return;
  }
  root.render(
    <React.StrictMode>
      <AuthProvider>
        <Shell id={id} components={components} />
      </AuthProvider>
    </React.StrictMode>,
  );
}
